import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * Custom hook to observe the intersection of a target element with the viewport.
 * It uses the Intersection Observer API to determine if the target element is in view.
 *
 * @param {number} threshold - A number between 0 and 1 indicating the percentage of the target's visibility required to trigger the observer.
 * @returns {[boolean, React.RefObject<HTMLDivElement>, React.RefObject<HTMLDivElement>]} - Returns a tuple containing:
 *   - A boolean indicating if the target element is intersecting or has been rendered.
 *   - A ref object to attach to the target element.
 *   - A ref object to attach to a buffer element.
 *
 * Example usage:
 * const [isVisible, targetRef, bufferRef] = useIntersectionObserver(0.5);
 */
export const useIntersectionObserver = (
  threshold = 0,
): [
  boolean,
  React.RefObject<HTMLDivElement>,
  React.RefObject<HTMLDivElement>,
] => {
  // State to track if the target element is intersecting
  const [isIntersecting, setIsIntersecting] = useState(false);
  // State to track if the target element has been rendered
  const [hasRendered, setHasRendered] = useState(false);
  // State to count the number of times the target has intersected
  const [count, setCount] = useState(0);

  // Ref to attach to the target element
  const targetRef = useRef<HTMLDivElement>(null);
  // Ref to attach to the buffer element
  const bufferRef = useRef<HTMLDivElement>(null);

  /**
   * Callback function to update the intersection state.
   * It is triggered whenever the intersection state of the target element changes.
   *
   * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries.
   */
  const updateIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      setIsIntersecting(entry.isIntersecting);

      if (entry.isIntersecting && !hasRendered) {
        if (count > 1) {
          setHasRendered(true);
          return;
        }

        setCount(prevCount => prevCount + 1);
      }
    },
    [count, hasRendered],
  );

  /**
   * Callback function to handle the observation of the buffer element.
   * Sets up the Intersection Observer and starts observing the buffer element.
   *
   * @returns {Function} - A cleanup function to unobserve the buffer element.
   */
  const handleObserve = useCallback(() => {
    const observer = new IntersectionObserver(updateIntersection, {
      threshold,
      rootMargin: '100px 0px 100px 0px',
    });

    const bufferElement = bufferRef.current;
    if (bufferElement) {
      observer.observe(bufferElement);
    }

    return () => {
      if (bufferElement) {
        observer.unobserve(bufferElement);
      }
    };
  }, [threshold, updateIntersection]);

  // Effect to start observing the buffer element when the component mounts and clean up when it unmounts
  useEffect(handleObserve, [threshold, handleObserve]);

  // Return the intersection state and refs for the target and buffer elements
  return [isIntersecting || hasRendered, targetRef, bufferRef];
};
